import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import { SPACING } from "../../common/constants/spacing";

const StatRow = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography variant="bodySm" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="bodySm" fontWeight={600} color="primary.main">
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h3" sx={{ mb: 4, fontWeight: 600 }}>
    {children}
  </Typography>
);

const DetailedStatsCards = ({ analytics }) => {
  const { t } = useTranslation("analytics");

  const totalRevenue =
    Number(analytics?.total_royalty_revenue || 0) +
    Number(analytics?.total_impression_revenue || 0);

  const platformSummary = analytics?.platform_summary || [];
  const currencyBreakdown = analytics?.currency_breakdown || [];
  const topPlatformRevenue = platformSummary[0]?.revenue || 1;

  return (
    <>
      <Typography
        variant="h2"
        fontWeight="bold"
        sx={{ mt: SPACING.lg, mb: SPACING.sm }}
      >
        {t("detailedStats.title")}
      </Typography>

      <Grid container spacing={3}>
        {/* Combined Totals */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>{t("detailedStats.combinedTotals")}</SectionTitle>
              <StatRow label={t("detailedStats.totalSalesCount")}>
                <AnimatedNumber
                  section="statsCards"
                  value={analytics?.total_sales_count}
                />
              </StatRow>
              <StatRow label={t("detailedStats.totalRoyaltyRevenue")}>
                <AnimatedNumber
                  section="statsCards"
                  value={analytics?.total_royalty_revenue}
                  isCurrency
                />
              </StatRow>
              <StatRow label={t("detailedStats.totalImpressionRevenue")}>
                <AnimatedNumber
                  section="statsCards"
                  value={analytics?.total_impression_revenue}
                  isCurrency
                />
              </StatRow>
              <StatRow label={t("detailedStats.combinedRevenue")}>
                <AnimatedNumber
                  section="statsCards"
                  value={totalRevenue}
                  isCurrency
                />
              </StatRow>
            </CardContent>
          </Card>
        </Grid>

        {/* Refunds */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>{t("detailedStats.refunds")}</SectionTitle>
              <StatRow label={t("detailedStats.refundCount")}>
                <AnimatedNumber
                  section="statsCards"
                  value={analytics?.refund_count}
                />
              </StatRow>
              <StatRow label={t("detailedStats.refundTotal")}>
                <Typography
                  variant="bodySm"
                  fontWeight={600}
                  color="error.main"
                >
                  <AnimatedNumber
                    section="statsCards"
                    value={analytics?.refund_total}
                    isCurrency
                  />
                </Typography>
              </StatRow>
              <StatRow label={t("detailedStats.netRevenue")}>
                <AnimatedNumber
                  section="statsCards"
                  value={
                    Number(analytics?.total_royalty_revenue || 0) -
                    Number(analytics?.refund_total || 0)
                  }
                  isCurrency
                />
              </StatRow>
            </CardContent>
          </Card>
        </Grid>

        {/* Currency Breakdown */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>
                {t("detailedStats.currencyBreakdown")}
              </SectionTitle>
              {currencyBreakdown.length > 0 ? (
                currencyBreakdown.map((c) => (
                  <StatRow
                    key={c.royalty_currency}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={c.royalty_currency || "N/A"}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="bodySm" color="text.secondary">
                          {c.count} sale{c.count !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    }
                  >
                    <AnimatedNumber
                      section="statsCards"
                      value={c.total}
                      prefix={c.royalty_currency === "USD" ? "$" : ""}
                      decimals={2}
                    />
                  </StatRow>
                ))
              ) : (
                <Typography
                  variant="bodySm"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  {t("detailedStats.noData")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Platform Revenue Summary */}
      {platformSummary.length > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <SectionTitle>{t("detailedStats.revenueByPlatform")}</SectionTitle>
            {platformSummary.map((p) => {
              const pct =
                totalRevenue > 0 ? (Number(p.revenue) / totalRevenue) * 100 : 0;
              return (
                <Box key={p.source_label} sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Chip
                        label={p.source_label}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="bodySm" color="text.secondary">
                        {p.count} sale{p.count !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        variant="bodySm"
                        color="primary.main"
                        fontWeight={600}
                      >
                        <AnimatedNumber
                          section="statsCards"
                          value={p.revenue}
                          isCurrency
                        />
                      </Typography>
                      <Typography variant="bodySm" color="text.secondary">
                        ({pct.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (Number(p.revenue) / Number(topPlatformRevenue)) * 100
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "action.hover",
                      "& .MuiLinearProgress-bar": { borderRadius: 3 },
                    }}
                  />
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DetailedStatsCards;
